/* Transferhistorie: Lesen und Schreiben der Tabelle player_transfers.
 *
 * Zugriffsregeln der Datenbank (supabase/migrations/20261004000000_player_transfers.sql):
 *   lesen                      admin, board, data alles; staff, guest nur Spieler,
 *                              die nicht 'released' sind
 *   anlegen, ändern, löschen   admin, board, data
 * Die Tabelle kennt keine Gebühr und keinen Weiterverkaufsanteil.
 *
 * Fehler werden hier zentral behandelt: Details in die Konsole, nach oben nur ein
 * allgemeiner Schlüssel ('loadFailed' / 'saveFailed' / 'notConnected').
 *
 * Klubregister: from_club_id und to_club_id (Verweis auf football_clubs) gibt es erst nach
 * supabase/klubregister-4-spalten.sql. Die App läuft davor und danach: Geschrieben wird mit den Spalten und,
 * wenn die Datenbank sie nicht kennt, ohne sie (clubColumns.js); der Text steht immer mit in der Zeile.
 *
 * Es werden nie Dateien gelöscht: Ein Logo kann von anderen Zeilen oder vom
 * aktuellen Verein weiter gebraucht werden.
 */

import { supabase } from './client.js';
import { rowToTransfer, transferToRow, transferClubIdsToRow, sortTransfers } from '../domain/transfers.js';
import { noteClubColumns, writeWithClubColumns } from './clubColumns.js';

/* Woraus gelesen wird. 'staff' = Ansicht player_transfers_staff (ohne note und created_by; Entscheidung
   Bastian 2026-10-15), sonst die Tabelle. Gesetzt von usePlayers.load, sobald die Rolle feststeht. Staff
   schreibt nie; Schreiben und Prüfungen unten bleiben auf der Tabelle. */
let readSource = 'full';

export function setTransfersSource(source) {
  readSource = source === 'staff' ? 'staff' : 'full';
}

/** Alle Einträge eines Spielers, neuestes zuerst. */
export async function listTransfers(playerId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from(readSource === 'staff' ? 'player_transfers_staff' : 'player_transfers')
    .select('*')
    .eq('player_id', playerId);
  if (error) {
    console.error('[transfers] Laden fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  if (data && data.length) noteClubColumns('transfers', data[0]);
  return sortTransfers((data || []).map(rowToTransfer));
}

/**
 * Welche dieser Pfade nennt die Transferhistorie ANDERER Spieler als Logo? (Schutz beim Löschen eines
 * Spielers: Ein Logo, das noch jemand anderes nutzt, bleibt.) Wirft bei Fehler.
 */
export async function foreignLogoRefs(paths, playerId) {
  if (!supabase) throw new Error('notConnected');
  if (!paths.length) return [];
  const list = paths.map((p) => '"' + String(p).replace(/"/g, '') + '"').join(',');
  const { data, error } = await supabase
    .from('player_transfers')
    .select('from_club_logo_path, to_club_logo_path')
    .neq('player_id', playerId)
    .or(`from_club_logo_path.in.(${list}),to_club_logo_path.in.(${list})`);
  if (error) {
    console.error('[transfers] Prüfung fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  return (data || []).flatMap((r) => [r.from_club_logo_path, r.to_club_logo_path]).filter(Boolean);
}

/** Neuen Eintrag anlegen; liefert den gespeicherten Eintrag (mit id und createdAt). */
export async function insertTransfer(playerId, transfer) {
  if (!supabase) throw new Error('notConnected');
  const row = transferToRow(transfer, playerId);
  const { data, error } = await writeWithClubColumns(
    'transfers',
    { ...row, ...transferClubIdsToRow(transfer) },
    row,
    (r) => supabase.from('player_transfers').insert(r).select().single()
  );
  if (error) {
    console.error('[transfers] Anlegen fehlgeschlagen:', error.message);
    throw new Error('saveFailed');
  }
  return rowToTransfer(data);
}

/** Eintrag ändern; liefert den gespeicherten Stand. */
export async function updateTransfer(id, playerId, transfer) {
  if (!supabase) throw new Error('notConnected');
  const row = transferToRow(transfer, playerId);
  const { data, error } = await writeWithClubColumns(
    'transfers',
    { ...row, ...transferClubIdsToRow(transfer) },
    row,
    (r) => supabase.from('player_transfers').update(r).eq('id', id).select()
  );
  /* Ohne Recht meldet die Datenbank keinen Fehler, sondern "0 Zeilen" — das darf
     nicht als Erfolg durchgehen. */
  if (error || !data || data.length !== 1) {
    console.error('[transfers] Ändern fehlgeschlagen:', error ? error.message : 'keine Zeile geändert');
    throw new Error('saveFailed');
  }
  return rowToTransfer(data[0]);
}

/** Eintrag löschen. Die Logo-Dateien bleiben im Speicher liegen. */
export async function deleteTransfer(id) {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase
    .from('player_transfers')
    .delete()
    .eq('id', id)
    .select('id');
  if (error || !data || data.length !== 1) {
    console.error('[transfers] Löschen fehlgeschlagen:', error ? error.message : 'keine Zeile gelöscht');
    throw new Error('saveFailed');
  }
}
